import React, { useEffect, useState } from 'react';
import { hrAPI } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, ArrowUp, ArrowDown, GraduationCap } from 'lucide-react';

export default function QualificationsPage() {
  const { user } = useAuth();
  const canManage = user?.role === 'admin' || user?.app_roles?.hr_training === 'manager';
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dlg, setDlg] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try { setRows((await hrAPI.listMaster('qualifications')).data || []); }
    catch { toast.error('Failed to load qualifications'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!dlg.name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      if (dlg.id) await hrAPI.updateMaster('qualifications', dlg.id, { name: dlg.name, description: dlg.description || '' });
      else await hrAPI.createMaster('qualifications', { name: dlg.name, description: dlg.description || '' });
      toast.success('Qualification saved'); setDlg(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete "${row.name}"?`)) return;
    try { await hrAPI.deleteMaster('qualifications', row.id); toast.success('Deleted'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed to delete'); }
  };

  const seed = async () => {
    try { const r = await hrAPI.seedQualifications(); toast.success(r.data.message); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const move = async (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= rows.length) return;
    const next = [...rows];
    [next[idx], next[j]] = [next[j], next[idx]];
    setRows(next); // optimistic
    try { await hrAPI.reorderQualifications(next.map(r => r.id)); load(); }
    catch { toast.error('Failed to reorder'); load(); }
  };

  return (
    <div className="space-y-5" data-testid="hr-qualifications-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-[#0F172A] flex items-center gap-2"><GraduationCap className="w-6 h-6 text-pink-600" /> Qualifications</h1>
          <p className="text-sm text-slate-500 mt-0.5">Define the qualification hierarchy (low → high). A higher qualification satisfies any lower minimum.</p>
        </div>
        {canManage && (
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={seed} data-testid="hr-qual-seed">Seed defaults</Button>
            <Button size="sm" className="bg-pink-600 hover:bg-pink-700 text-white gap-1" onClick={() => setDlg({ name: '', description: '' })} data-testid="hr-qual-add"><Plus className="w-4 h-4" /> Add Qualification</Button>
          </div>
        )}
      </div>

      <Card className="border border-slate-200 shadow-none"><CardContent className="p-0">
        <Table>
          <TableHeader><TableRow className="bg-slate-50/80">
            <TableHead className="text-xs w-20">Level</TableHead>
            <TableHead className="text-xs">Name</TableHead>
            <TableHead className="text-xs">Description</TableHead>
            {canManage && <TableHead className="text-xs w-40 text-right">Actions</TableHead>}
          </TableRow></TableHeader>
          <TableBody>
            {loading ? <TableRow><TableCell colSpan={4} className="text-center text-slate-400 py-8 text-sm">Loading...</TableCell></TableRow> :
              rows.length === 0 ? <TableRow><TableCell colSpan={4} className="text-center text-slate-400 py-8 text-sm">No qualifications yet. Click "Seed defaults" to start.</TableCell></TableRow> :
                rows.map((r, i) => (
                  <TableRow key={r.id} data-testid={`hr-qual-row-${r.id}`}>
                    <TableCell><Badge variant="outline" className="text-[11px] font-mono bg-pink-50 text-pink-700 border-pink-200">L{r.rank ?? i + 1}</Badge></TableCell>
                    <TableCell className="text-sm font-medium">{r.name}</TableCell>
                    <TableCell className="text-sm text-slate-500">{r.description || '—'}</TableCell>
                    {canManage && (
                      <TableCell className="text-right whitespace-nowrap">
                        <Button size="icon" variant="ghost" className="h-7 w-7" disabled={i === 0} onClick={() => move(i, -1)} data-testid={`hr-qual-up-${r.id}`}><ArrowUp className="w-3.5 h-3.5" /></Button>
                        <Button size="icon" variant="ghost" className="h-7 w-7" disabled={i === rows.length - 1} onClick={() => move(i, 1)} data-testid={`hr-qual-down-${r.id}`}><ArrowDown className="w-3.5 h-3.5" /></Button>
                        <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setDlg({ id: r.id, name: r.name, description: r.description || '' })} data-testid={`hr-qual-edit-${r.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                        <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500" onClick={() => remove(r)} data-testid={`hr-qual-del-${r.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
          </TableBody>
        </Table>
      </CardContent></Card>

      <Dialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent data-testid="hr-qual-dialog">
          <DialogHeader>
            <DialogTitle>{dlg?.id ? 'Edit Qualification' : 'Add Qualification'}</DialogTitle>
            <DialogDescription className="text-xs">New qualifications are added at the top level; use the up/down arrows to set the hierarchy.</DialogDescription>
          </DialogHeader>
          {dlg && (
            <div className="space-y-3">
              <div><Label className="text-xs">Name *</Label><Input value={dlg.name} onChange={e => setDlg({ ...dlg, name: e.target.value })} data-testid="hr-qual-name-input" autoFocus /></div>
              <div><Label className="text-xs">Description</Label><Textarea rows={2} value={dlg.description} onChange={e => setDlg({ ...dlg, description: e.target.value })} data-testid="hr-qual-desc-input" /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlg(null)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-pink-600 hover:bg-pink-700 text-white" data-testid="hr-qual-save">{saving ? 'Saving...' : 'Save'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
